import serviceStormRestoration from "@/assets/service-storm-restoration.jpg";

export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  image: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

/**
 * Client-supplied project photos for R&R House Renovation LLC.
 *
 * Originals are 2–5 MB, so they go through Supabase's render endpoint, which
 * resizes and negotiates WebP (~50–130 KB).
 *
 * NOTE: the render endpoint must be given BOTH width and height. Passing
 * width alone makes it keep the source height and stretch the image badly.
 * `clientImage()` derives the height from the source's native aspect ratio —
 * pass the ratio that matches the file, and keep `width` at or below the
 * source width so nothing is upscaled.
 */
const CLIENT_RENDER =
  "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1/render/image/public/client-media/r-r-house-renovation-llc/images";

export const clientImage = (file: string, width: number, ratio: number) =>
  `${CLIENT_RENDER}/${file}?width=${width}&height=${Math.round(width / ratio)}&resize=cover&quality=76`;

const RATIO_4_3 = 4 / 3; // 5712x4284 phone photos
const RATIO_3_2 = 3 / 2; // 1536x1024 files

/**
 * Builds a `srcset` for a client photo so a phone downloads a phone-sized file
 * rather than the desktop one. The widths mirror `clientImage()`, which needs
 * an explicit height alongside the width.
 */
export const clientImageSrcSet = (file: string, ratio: number, widths: number[]) =>
  widths.map((w) => `${clientImage(file, w, ratio)} ${w}w`).join(", ");

/**
 * Widths offered for the service/blog card grids. The largest card is about
 * 620 CSS px, so 1200 covers a 2x screen; the smaller steps are what phones
 * and tablets actually download.
 */
export const CARD_WIDTHS = [400, 640, 900, 1200] as const;

/** `sizes` for a two-column card grid inside the 1280px container. */
export const CARD_SIZES = "(min-width: 1280px) 620px, (min-width: 768px) 50vw, 100vw";

/**
 * Derives a `srcset` from an image URL that already encodes its width.
 *
 * Both CDNs used here take the width in the query string, so one helper covers
 * the whole site without the data file having to carry a second representation
 * of every photo:
 *   - Unsplash takes `w` and derives the height from `fit=crop`.
 *   - Supabase's render endpoint needs `width` AND `height`; passing width
 *     alone makes it keep the source height and stretch the image, so the
 *     height is scaled to match.
 *
 * Widths above the original are dropped — upscaling only wastes bytes.
 */
export const imageSrcSet = (url: string, widths: readonly number[] = CARD_WIDTHS) => {
  const [base, query = ""] = url.split("?");
  const params = new URLSearchParams(query);

  const supabaseWidth = Number(params.get("width"));
  const supabaseHeight = Number(params.get("height"));
  if (supabaseWidth && supabaseHeight) {
    const ratio = supabaseWidth / supabaseHeight;
    return widths
      .filter((w) => w <= supabaseWidth)
      .map((w) => {
        const next = new URLSearchParams(params);
        next.set("width", String(w));
        next.set("height", String(Math.round(w / ratio)));
        return `${base}?${next.toString()} ${w}w`;
      })
      .join(", ");
  }

  const unsplashWidth = Number(params.get("w"));
  if (unsplashWidth) {
    return widths
      .filter((w) => w <= unsplashWidth)
      .map((w) => {
        const next = new URLSearchParams(params);
        next.set("w", String(w));
        return `${base}?${next.toString()} ${w}w`;
      })
      .join(", ");
  }

  return "";
};

/**
 * The home page hero photo. index.html preloads exactly these URLs so the
 * browser can start the LCP fetch before React has booted — keep the two in
 * step if this ever changes.
 */
export const heroPhoto = {
  file: "1786068955842-839-IMG_0453.jpeg",
  ratio: RATIO_4_3,
  widths: [900, 1400, 1800],
  width: 1800,
  height: 1350,
} as const;

export const clientPhotos = {
  /** Wide view of a completed kitchen remodel — white shaker cabinets, quartz, LVP. */
  kitchenRemodelWide: clientImage("1786068954891-420-IMG_0451.jpeg", 1800, RATIO_4_3),
  /** Quartz-topped island and cabinetry from the same completed kitchen. */
  kitchenIsland: clientImage("1786068955842-839-IMG_0453.jpeg", 1800, RATIO_4_3),
  /** Driveway pour in front of a finished home. */
  concreteDriveway: clientImage(
    "1786068952873-115-D2BD8C09-2698-4526-9CB5-0D70A0E26B1A.png",
    1400,
    RATIO_3_2
  ),
} as const;

/**
 * Stock photography — real photographs only (no illustrations, renders, or
 * AI-generated imagery), each matched to the service it illustrates and used
 * exactly once across the site.
 *
 * Selection rules, in priority order:
 *  1. No identifiable people. No recognisable faces anywhere.
 *  2. No third-party branding — no logos on tools, workwear, vehicles or signage.
 *  3. Subject must read as the service at a glance, at card size, under the
 *     dark overlay gradient.
 *  4. Prefer the finished result or the work surface itself over crew shots;
 *     it photographs cleaner and keeps the set visually consistent.
 * Every candidate below was viewed at full size before being selected.
 */
const stock = (id: string, w = 1600) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=76`;

export const stockPhotos = {
  /** Hands signing claim paperwork — no face, no letterhead. */
  insuranceClaim: stock("photo-1450101499163-c8848c66ca85"),
  /**
   * Torn-out storm/water-damaged ceiling and walls with new framing going
   * back in — covers both halves of the service: the damage and the rebuild.
   * AI-generated project asset (local file, so no srcset variants exist).
   */
  stormDamage: serviceStormRestoration,
  /** Roller applying fresh paint to an interior wall. */
  painting: stock("photo-1562259949-e8e7689d7828"),
  /** Empty room finished with wide-plank wood flooring. */
  flooring: stock("photo-1722650362309-2f2fdffbfbf1"),
  /** Aerial view of a suburban home with a full architectural shingle roof. */
  roofing: stock("photo-1755114203680-d39d95efa82c"),
  /** Roof truss and wall framing erected against a clear sky. */
  carpentry: stock("photo-1676802037786-3697d60497ae"),
  /** Renovated open-concept kitchen, living and dining space. */
  homeRenovation: stock("photo-1770751188011-8ab6c81552bf"),
  /** Renovated bathroom — walk-in tiled shower and double vanity. */
  bathroom: stock("photo-1763485955497-f5ef5d178698"),

  /* Non-service surfaces */
  blueprints: stock("photo-1762146828422-50a8bd416d3c"), // About hero
  livingRoom: stock("photo-1615873968403-89e068629265"), // Services page hero
  stormSky: stock("photo-1758795293214-5aad2ee3735a"), // blog: hail claims
  bathroomTub: stock("photo-1620626011761-996317b8d101"), // blog: water damage
  kitchenStock: stock("photo-1560185127-1902ccdc5094"), // blog: kitchen remodel planning
} as const;

export const services: ServiceData[] = [
  {
    slug: "insurance-claims",
    title: "Insurance Claims Specialist",
    subtitle: "We handle the claim from inspection to final repair",
    price: "Free Inspection",
    image: stockPhotos.insuranceClaim,
    description:
      "Filing a property damage claim in Texas is confusing, slow, and easy to get wrong. R&R House Renovation LLC works claims every week across Denton County and the DFW metroplex — we document the damage, meet your adjuster on site, and make sure the scope of loss reflects the real cost of putting your home back together. Then we do the repairs ourselves, so nothing gets lost between the estimate and the finished job.",
    features: [
      "Free, no-obligation damage inspection & photo documentation",
      "We meet your insurance adjuster on site",
      "Detailed line-item scope of loss written for your carrier",
      "Supplement requests when the initial estimate falls short",
      "Hail, wind, water, fire & storm claims",
      "One contractor from claim through final walkthrough",
    ],
    process: [
      { step: "Inspection", desc: "We walk the property, photograph every affected area, and tell you honestly whether you have a claim." },
      { step: "Documentation", desc: "You get a written, line-item scope of loss your carrier can act on." },
      { step: "Adjuster Meeting", desc: "We meet your adjuster on site so nothing gets missed or undervalued." },
      { step: "Repairs", desc: "Once the claim is approved, our own crews complete the work start to finish." },
    ],
  },
  {
    slug: "water-fire-storm-restoration",
    title: "Water, Fire & Storm Damage Restoration",
    subtitle: "Emergency response and full property rebuild",
    price: "Free Estimate",
    image: stockPhotos.stormDamage,
    description:
      "North Texas weather does not wait for a convenient time. Burst pipes, hail, straight-line winds, roof leaks, kitchen fires and smoke damage all get worse by the hour. We respond fast, stop the damage from spreading, dry the structure out properly, and then rebuild — drywall, framing, flooring, paint, cabinets, roofing — until the home looks like nothing ever happened.",
    features: [
      "Water extraction, structural drying & dehumidification",
      "Mold-prevention treatment on affected framing & drywall",
      "Fire, soot & smoke odor remediation",
      "Hail, wind & storm damage repair",
      "Full reconstruction — framing, drywall, flooring, paint",
      "Insurance claim documentation included",
    ],
    process: [
      { step: "Rapid Response", desc: "We get on site quickly to stop the source and limit how far the damage spreads." },
      { step: "Dry & Stabilize", desc: "Professional extraction and drying equipment brings moisture levels back to normal." },
      { step: "Scope & Claim", desc: "Every affected material is documented and priced for your insurance carrier." },
      { step: "Rebuild", desc: "We restore the space completely — structure, finishes, and everything in between." },
    ],
  },
  {
    slug: "interior-exterior-painting",
    title: "Interior & Exterior Painting",
    subtitle: "Clean lines, full prep, finishes that last",
    price: "Free Estimate",
    image: stockPhotos.painting,
    description:
      "A paint job is only as good as the prep underneath it. We patch, caulk, sand, and prime before a single finish coat goes on — inside and out. Texas sun and hail are hard on exteriors, so we use premium exterior-grade products rated for this climate, and we mask and protect your floors, fixtures, and landscaping like it is our own house.",
    features: [
      "Full surface prep — patching, caulking, sanding & priming",
      "Interior walls, ceilings, trim, doors & cabinets",
      "Exterior siding, stucco, brick, fascia & soffit",
      "Premium UV- and weather-rated exterior coatings",
      "Drywall & texture repair before painting",
      "Complete masking, protection & daily cleanup",
    ],
    process: [
      { step: "Color Consult", desc: "We help you land on colors and sheens that fit the room and the light." },
      { step: "Prep Work", desc: "Repairs, caulking, sanding, and priming — the part that makes paint last." },
      { step: "Application", desc: "Clean, even coats with crisp cut lines and no shortcuts on coverage." },
      { step: "Walkthrough", desc: "We walk the job with you and touch up anything before we pack out." },
    ],
  },
  {
    slug: "flooring-installation",
    title: "Flooring — Tile, Wood, Laminate & Vinyl",
    subtitle: "Level subfloors, tight seams, clean transitions",
    price: "Free Estimate",
    image: stockPhotos.flooring,
    description:
      "Flooring is the fastest way to change how a whole house feels — and the easiest place to spot bad workmanship. We level and prep the subfloor first, lay out the room so cuts land where they should, and finish with proper transitions, trim, and grout lines that stay straight. Tile, hardwood, engineered wood, laminate, and luxury vinyl plank, in homes across DFW.",
    features: [
      "Ceramic, porcelain & natural stone tile",
      "Solid hardwood & engineered wood",
      "Laminate & luxury vinyl plank (LVP)",
      "Subfloor leveling, prep & moisture barriers",
      "Baseboard, quarter-round & transition trim",
      "Old flooring tear-out & haul-away",
    ],
    process: [
      { step: "Measure", desc: "We measure the space and give you an honest material and labor estimate." },
      { step: "Subfloor Prep", desc: "Tear-out, leveling, and moisture control so the new floor sits right." },
      { step: "Layout & Install", desc: "Rooms are dry-laid first so seams and cuts land in the right places." },
      { step: "Trim & Finish", desc: "Transitions, baseboards, and grout finish work — then a full cleanup." },
    ],
  },
  {
    slug: "concrete",
    title: "Concrete",
    subtitle: "Driveways, patios, walkways & slabs",
    price: "Free Estimate",
    image: clientPhotos.concreteDriveway,
    description:
      "Concrete in North Texas has to handle expansive clay soil, hard freezes, and 100-degree summers. We prepare the base properly, set forms true, use the right reinforcement and mix for the job, and cut control joints where the slab actually wants to crack. Driveways, patios, sidewalks, garage slabs, and foundations for additions.",
    features: [
      "Driveways, approaches & garage slabs",
      "Patios, walkways & pool decks",
      "Slabs for additions, sheds & outbuildings",
      "Proper base prep, forming & rebar reinforcement",
      "Control joints cut to prevent random cracking",
      "Broom, smooth & stamped finish options",
    ],
    process: [
      { step: "Site Prep", desc: "Demo, excavation, and a compacted base built for North Texas soil." },
      { step: "Form & Reinforce", desc: "Forms set to grade with rebar or mesh sized for the load." },
      { step: "Pour & Finish", desc: "Placed, screeded, and finished to the texture you picked." },
      { step: "Cure & Joint", desc: "Control joints cut and the slab cured properly before you use it." },
    ],
  },
  {
    slug: "roofing",
    title: "Roofing",
    subtitle: "Hail damage, replacements & leak repair",
    price: "Free Inspection",
    image: stockPhotos.roofing,
    description:
      "DFW takes more hail than almost anywhere in the country, and most roof claims here are won or lost on documentation. We inspect for free, tell you straight whether the damage is claimable, and handle the carrier side if it is. Full replacements, storm repairs, decking, underlayment, flashing, and ventilation — with crews that clean up every nail before they leave.",
    features: [
      "Free hail & storm damage roof inspections",
      "Full roof replacement — architectural & 3-tab shingles",
      "Leak detection & targeted repair",
      "Decking replacement, underlayment & ice/water shield",
      "Flashing, valleys, vents & ridge ventilation",
      "Magnetic nail sweep & full-property cleanup",
    ],
    process: [
      { step: "Free Inspection", desc: "We get on the roof, document conditions, and show you the photos." },
      { step: "Claim Support", desc: "If it is storm damage, we handle the paperwork and meet the adjuster." },
      { step: "Tear-Off & Deck", desc: "Old material removed, decking checked and replaced where it is soft." },
      { step: "Install & Clean", desc: "New system installed to spec, then a full magnetic sweep of the property." },
    ],
  },
  {
    slug: "carpentry",
    title: "Carpentry",
    subtitle: "Framing, trim, cabinets & custom built-ins",
    price: "Free Estimate",
    image: stockPhotos.carpentry,
    description:
      "Good carpentry is what separates a remodel that looks finished from one that looks close. Our carpenters handle structural framing and rot repair as comfortably as they handle crown molding, custom shelving, and cabinet installs. If it is wood and it has to be square, level, and tight, this is the crew.",
    features: [
      "Framing, structural repair & wall modifications",
      "Rotted wood, fascia, soffit & siding replacement",
      "Interior trim, crown molding & wainscoting",
      "Custom built-ins, shelving & closet systems",
      "Cabinet installation & refacing",
      "Doors, windows & stair repair",
    ],
    process: [
      { step: "Assessment", desc: "We look at the structure behind the finish before quoting anything." },
      { step: "Design", desc: "Dimensions, materials, and profiles confirmed with you up front." },
      { step: "Build", desc: "Cut and installed on site so everything fits the actual room, not a drawing." },
      { step: "Finish", desc: "Filled, sanded, and prepped for paint or stain — ready to look at." },
    ],
  },
  {
    slug: "home-renovation",
    title: "Home Renovation",
    subtitle: "Whole-home remodels, additions & open concepts",
    price: "Free Consultation",
    image: stockPhotos.homeRenovation,
    description:
      "Whole-home renovations are where every trade we run comes together — demo, framing, flooring, carpentry, paint, and finish work under one contractor. We open up closed floor plans, modernize dated houses, finish out additions, and take on the projects other contractors want to split into five separate jobs. One point of contact, one schedule, one crew you recognize.",
    features: [
      "Whole-home & multi-room renovations",
      "Wall removal & open-concept conversions",
      "Room additions & garage conversions",
      "Drywall, texture, trim & paint packages",
      "Flooring, lighting & fixture upgrades",
      "One contractor managing every trade",
    ],
    process: [
      { step: "Consultation", desc: "We walk the home, listen to what you want, and talk honestly about budget." },
      { step: "Scope & Plan", desc: "A written scope with materials, phases, and a realistic schedule." },
      { step: "Build", desc: "Demo through finish work, with the site protected and cleaned daily." },
      { step: "Final Walk", desc: "We punch out the list with you before we call it done." },
    ],
  },
  {
    slug: "bathroom-renovation",
    title: "Bathroom Renovation",
    subtitle: "Tile showers, vanities & full gut remodels",
    price: "Free Estimate",
    image: stockPhotos.bathroom,
    description:
      "Bathrooms are small rooms with no margin for error — waterproofing, slope, and tile layout have to be right the first time. We build tile showers on properly waterproofed substrates, set vanities and fixtures level, and finish with grout and caulk lines that still look good years later. Tub-to-shower conversions, accessibility upgrades, and full gut remodels.",
    features: [
      "Full gut remodels & tub-to-shower conversions",
      "Custom tile showers with proper waterproofing",
      "Vanities, countertops, sinks & fixtures",
      "Floor tile, niches, benches & glass enclosures",
      "Exhaust ventilation & moisture control",
      "Accessible & walk-in shower conversions",
    ],
    process: [
      { step: "Design", desc: "Layout, tile, and fixture selections settled before demo starts." },
      { step: "Demo & Rough", desc: "Tear-out plus any plumbing and electrical moves the new layout needs." },
      { step: "Waterproof & Tile", desc: "Substrate waterproofed correctly, then tile set to a planned layout." },
      { step: "Finish", desc: "Fixtures, glass, paint, and trim — then a clean, ready-to-use bathroom." },
    ],
  },
  {
    slug: "kitchen-renovation",
    title: "Kitchen Renovation",
    subtitle: "Cabinets, countertops, islands & full rebuilds",
    price: "Free Consultation",
    image: clientPhotos.kitchenRemodelWide,
    description:
      "The kitchen is the project that changes how a house lives — and the one homeowners regret rushing. We handle cabinets, countertops, islands, backsplash, lighting, flooring, and the plumbing and electrical moves behind them. Layouts get planned around how you actually cook, and we keep the job moving so your kitchen is not out of service longer than it has to be.",
    features: [
      "Custom & semi-custom cabinet installation",
      "Quartz, granite & solid surface countertops",
      "Islands, peninsulas & added storage",
      "Backsplash tile & under-cabinet lighting",
      "Appliance, sink & plumbing relocation",
      "Flooring, paint & trim to finish the space",
    ],
    process: [
      { step: "Layout", desc: "We plan the kitchen around how you cook, not just what fits." },
      { step: "Selections", desc: "Cabinets, counters, tile, and fixtures picked and ordered early." },
      { step: "Demo & Install", desc: "Tear-out, rough-in, cabinets, counters, and tile in the right order." },
      { step: "Finish", desc: "Hardware, lighting, paint, and a final clean before you cook in it." },
    ],
  },
];
